// Helpers for repairing and merging duplicate Spell Lists.
//
// SpellList.toLight() used to be toDBObj(), so its _light copies had no true_id and
// loaded under the light doc's own _id. Saving one of those created a brand new full
// doc under that id (updateObject inserts when nothing matches) plus another light
// copy, and deleting one missed both. The copies are chained together by id, so
// groupSpellLists follows those links rather than trusting names.
//
// Everything here works on raw stored documents, not models.

const OBJECT_ID = /^[0-9a-f]{24}$/i;

// Seconds since epoch from an ObjectId, or 0 when it isn't one. Newer ids are newer copies.
export const objectIdTime = (id: any): number =>
  typeof id === "string" && OBJECT_ID.test(id) ? parseInt(id.slice(0, 8), 16) : 0;

const newestFirst = (a: any, b: any) => objectIdTime(b._id) - objectIdTime(a._id);

export const normalizeName = (name: any) => (typeof name === "string" ? name : "").trim().toLowerCase();

export interface SpellListGroup {
  key: string;
  name: string;
  fulls: any[];
  lights: any[];
  // Every _id in the group, full and light (a light can share its full doc's _id).
  ids: string[];
  // Exactly one full doc and one light copy, correctly pointing at each other.
  healthy: boolean;
}

// Connected components over full and light docs, linked by light_id, true_id and
// a light doc sharing a full doc's _id.
export const groupSpellLists = (fulls: any[], lights: any[]): SpellListGroup[] => {
  const parent = new Map<string, string>();
  const find = (k: string): string => {
    let root = k;
    while (parent.get(root) !== root) {
      root = parent.get(root)!;
    }
    parent.set(k, root);
    return root;
  };
  const union = (a: string, b: string) => {
    const ra = find(a);
    const rb = find(b);
    if (ra !== rb) {
      parent.set(ra, rb);
    }
  };
  const full_ids = new Set(fulls.map(f => f._id));
  const light_ids = new Set(lights.map(l => l._id));
  fulls.forEach(f => parent.set(`F:${f._id}`, `F:${f._id}`));
  lights.forEach(l => parent.set(`L:${l._id}`, `L:${l._id}`));
  for (const f of fulls) {
    if (f.light_id && light_ids.has(f.light_id)) {
      union(`F:${f._id}`, `L:${f.light_id}`);
    }
    if (light_ids.has(f._id)) {
      union(`F:${f._id}`, `L:${f._id}`);
    }
  }
  for (const l of lights) {
    if (l.true_id && full_ids.has(l.true_id)) {
      union(`L:${l._id}`, `F:${l.true_id}`);
    }
    // A light copy saved through the old toLight carried its full doc's light_id.
    if (l.light_id && light_ids.has(l.light_id)) {
      union(`L:${l._id}`, `L:${l.light_id}`);
    }
  }

  const groups = new Map<string, SpellListGroup>();
  const groupFor = (k: string) => {
    const root = find(k);
    let group = groups.get(root);
    if (!group) {
      group = { key: root, name: "", fulls: [], lights: [], ids: [], healthy: false };
      groups.set(root, group);
    }
    return group;
  };
  fulls.forEach(f => groupFor(`F:${f._id}`).fulls.push(f));
  lights.forEach(l => groupFor(`L:${l._id}`).lights.push(l));

  return Array.from(groups.values()).map(group => {
    group.fulls.sort(newestFirst);
    group.lights.sort(newestFirst);
    const ids = new Set<string>();
    [...group.fulls, ...group.lights].forEach(d => ids.add(d._id));
    group.ids = Array.from(ids);
    group.name = (group.fulls[0] ?? group.lights[0])?.name ?? "";
    const [full] = group.fulls;
    const [light] = group.lights;
    group.healthy = group.fulls.length === 1 && group.lights.length === 1 &&
      light.true_id === full._id && full.light_id === light._id && light._id !== full._id;
    return group;
  });
};

// Fields on a SpellLimitation that are sets of ids: merging unions them.
// Everything else on a limit is a filter setting, where a union would change its meaning.
const ID_ARRAY_FIELDS = ["spell_ids", "excluded_spell_ids", "source_ids", "list_ids"];

const unique = (values: any[]) => Array.from(new Set(values));

const limitSettings = (limit: any) => {
  const copy = { ...limit };
  delete copy.true_id;
  ID_ARRAY_FIELDS.forEach(f => delete copy[f]);
  return copy;
};

const sbLabel = (sb_id: string, sbName?: (sb_id: string) => string) => sbName ? sbName(sb_id) : sb_id;

export interface SpellListMergeResult {
  // Top-level fields to write on the kept full doc.
  fields: {
    name: string;
    description: string;
    notes: string;
    source_books: any[];
    limit_hash_array: Array<{ source_book_id: string, limit: any }>;
    major_limits: string[];
  };
  // Settings that differed between copies; the newest copy's value was kept.
  conflicts: string[];
}

// Merges every copy (full and light) into one. Spells and other id lists are unioned
// per source book; any other limit setting comes from the newest copy that has that
// source book, and differences are reported.
export const mergeSpellListDocs = (docs: any[], keeper: any, sbName?: (sb_id: string) => string): SpellListMergeResult => {
  const ordered = [...docs].sort(newestFirst);
  const conflicts: string[] = [];

  const by_sb = new Map<string, any[]>();
  for (const doc of ordered) {
    for (const entry of (doc.limit_hash_array ?? []) as any[]) {
      if (!entry || typeof entry.source_book_id !== "string" || entry.source_book_id === "" || !entry.limit) {
        continue;
      }
      if (!by_sb.has(entry.source_book_id)) {
        by_sb.set(entry.source_book_id, []);
      }
      by_sb.get(entry.source_book_id)!.push(entry.limit);
    }
  }
  const limit_hash_array: Array<{ source_book_id: string, limit: any }> = [];
  by_sb.forEach((limits, source_book_id) => {
    const [base] = limits;
    const limit = { ...base };
    for (const field of ID_ARRAY_FIELDS) {
      limit[field] = unique(limits.flatMap(l => Array.isArray(l[field]) ? l[field] : []));
    }
    const base_settings = JSON.stringify(limitSettings(base));
    if (limits.some(l => JSON.stringify(limitSettings(l)) !== base_settings)) {
      conflicts.push(`${sbLabel(source_book_id, sbName)}: limit settings differ between copies (kept the newest)`);
    }
    limit_hash_array.push({ source_book_id, limit });
  });

  const source_books: any[] = [];
  const seen_sbs = new Set<string>();
  for (const doc of [keeper, ...ordered]) {
    for (const sb of (doc.source_books ?? []) as any[]) {
      const id = sb?.id;
      if (typeof id === "string" && !seen_sbs.has(id)) {
        seen_sbs.add(id);
        source_books.push(sb);
      }
    }
  }

  const firstText = (field: string) => {
    if (typeof keeper[field] === "string" && keeper[field] !== "") {
      return keeper[field];
    }
    return ordered.map(d => d[field]).find(v => typeof v === "string" && v !== "") ?? "";
  };
  const names = unique(ordered.map(d => (d.name ?? "").trim()).filter(n => n !== ""));
  if (names.length > 1) {
    conflicts.push(`Names differ: ${names.join(" / ")} (kept ${keeper.name})`);
  }

  return {
    fields: {
      name: keeper.name ?? "",
      description: firstText("description"),
      notes: firstText("notes"),
      source_books,
      limit_hash_array,
      major_limits: unique(ordered.flatMap(d => Array.isArray(d.major_limits) ? d.major_limits : []))
        .filter(sb_id => by_sb.has(sb_id)),
    },
    conflicts,
  };
};

// A doc's own identity fields, never references to something else.
const OWN_ID_KEYS = new Set(["_id", "light_id", "true_id"]);

export interface IdRefChanges {
  // Top-level fields of the doc to $set, with the ids already swapped.
  fields: Record<string, any>;
  // How many individual references were swapped.
  count: number;
}

const swapDeep = (value: any, id_map: Map<string, string>, counter: { count: number }): any => {
  if (typeof value === "string") {
    const to = id_map.get(value);
    if (to !== undefined) {
      counter.count++;
      return to;
    }
    return value;
  }
  if (Array.isArray(value)) {
    const before = counter.count;
    const swapped = value.map(o => swapDeep(o, id_map, counter));
    // ["old", "kept"] would otherwise become ["kept", "kept"].
    return counter.count > before && swapped.every(o => typeof o === "string") ? unique(swapped) : swapped;
  }
  if (value && typeof value === "object") {
    const copy: any = {};
    for (const key of Object.keys(value)) {
      copy[key] = swapDeep(value[key], id_map, counter);
    }
    return copy;
  }
  return value;
};

// Swaps every string equal to an old id, at any depth, whatever the field is called
// (list_ids, spell_list_ids, spell_list, spell_list_id, extra_spell_lists...).
// ObjectIds are unique across collections, so an exact match is a real reference.
export const replaceIdRefs = (doc: any, id_map: Map<string, string>): IdRefChanges => {
  const fields: Record<string, any> = {};
  let count = 0;
  if (!doc || id_map.size === 0) {
    return { fields, count };
  }
  for (const key of Object.keys(doc)) {
    if (OWN_ID_KEYS.has(key)) {
      continue;
    }
    const counter = { count: 0 };
    const swapped = swapDeep(doc[key], id_map, counter);
    if (counter.count > 0) {
      fields[key] = swapped;
      count += counter.count;
    }
  }
  return { fields, count };
};

// id -> how many references to it the docs contain (same rules as replaceIdRefs).
export const countIdRefs = (docs: any[], ids: Iterable<string>): Map<string, number> => {
  const wanted = new Set(ids);
  const counts = new Map<string, number>();
  const walk = (value: any) => {
    if (typeof value === "string") {
      if (wanted.has(value)) {
        counts.set(value, (counts.get(value) ?? 0) + 1);
      }
    } else if (Array.isArray(value)) {
      value.forEach(walk);
    } else if (value && typeof value === "object") {
      Object.values(value).forEach(walk);
    }
  };
  for (const doc of docs) {
    for (const key of Object.keys(doc ?? {})) {
      if (!OWN_ID_KEYS.has(key)) {
        walk(doc[key]);
      }
    }
  }
  return counts;
};
